import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  ArrowRight,
  Users,
  Phone,
  FileCheck,
  CalendarCheck,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';
import { formatDateOnly, parseApiTimestamp } from '@/lib/datetime';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { Skeleton } from '@/components/ui/skeleton';
import { CardGridSkeleton } from '@/components/ui/data-table-skeleton';
import { useOrganizationsStore } from '@/stores/organizationsStore';
import { api } from '@/lib/api';
import type { CallRecord } from '@/types/callFilters';

interface DashboardStats {
  patients: number;
  apcmConsentedPatients: number;
  approvedPlansOfCare: number;
  checkinsThisMonth: number;
  totalCalls: number;
  consentedByState: number;
  approvedByState: number;
  checkinsCompletedByState: number;
}

interface DailyCount {
  date: string;
  label: string;
  count: number;
}

function groupCallsByDay(calls: CallRecord[], days: number): DailyCount[] {
  const buckets: Record<string, number> = {};
  for (let i = days - 1; i >= 0; i--) {
    const key = format(subDays(new Date(), i), 'yyyy-MM-dd');
    buckets[key] = 0;
  }

  for (const call of calls) {
    const dateStr = call.call_started_at || call.created_at;
    if (!dateStr) continue;
    const at = parseApiTimestamp(dateStr);
    if (!at) continue;
    const key = format(startOfDay(at), 'yyyy-MM-dd');
    if (key in buckets) {
      buckets[key]++;
    }
  }

  return Object.entries(buckets).map(([date, count]) => ({
    date,
    label: formatDateOnly(date, 'EEE'),
    count,
  }));
}

function DailyBarChart({
  title,
  data,
  color,
}: {
  title: string;
  data: DailyCount[];
  color: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="min-w-0">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={data}
            margin={{ top: 5, right: 10, left: -10, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" fontSize={12} tickLine={false} />
            <YAxis fontSize={12} tickLine={false} allowDecimals={false} />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const item = payload[0].payload as DailyCount;
                return (
                  <div className="bg-popover border rounded-lg shadow-lg p-2 text-sm">
                    <p className="font-medium">
                      {formatDateOnly(item.date, 'MMM d')}
                    </p>
                    <p className="text-muted-foreground">{item.count} calls</p>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function ChartSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-40" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[200px] w-full" />
      </CardContent>
    </Card>
  );
}

export function DashboardEmptyState() {
  const { selectedOrganizationId } = useOrganizationsStore();
  // Stats arrive in under a second; the two call/query requests behind the
  // charts take 4-5s each on a large org. Loading them separately means the
  // page shows numbers almost immediately instead of a skeleton for 5s+.
  const [statsLoading, setStatsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [consentsByDay, setConsentsByDay] = useState<DailyCount[]>([]);
  const [checkinsByDay, setCheckinsByDay] = useState<DailyCount[]>([]);

  useEffect(() => {
    if (!selectedOrganizationId) {
      setStatsLoading(false);
      setChartsLoading(false);
      return;
    }
    let cancelled = false;
    setStatsLoading(true);
    setChartsLoading(true);

    api
      .getAPCMStats(selectedOrganizationId)
      .catch(() => null)
      .then((apcmStats) => {
        if (cancelled) return;
        setStats(
          apcmStats
            ? {
                patients: apcmStats.patients ?? 0,
                apcmConsentedPatients: apcmStats.apcm_consented_patients ?? 0,
                approvedPlansOfCare: apcmStats.approved_plans_of_care ?? 0,
                checkinsThisMonth: apcmStats.checkins_this_month ?? 0,
                totalCalls: apcmStats.total_calls ?? 0,
                consentedByState: apcmStats.consented_by_state ?? 0,
                approvedByState: apcmStats.approved_by_state ?? 0,
                checkinsCompletedByState: apcmStats.checkins_completed_by_state ?? 0,
              }
            : null,
        );
        setStatsLoading(false);
      });

    Promise.all([
      api
        .queryCallsV3(selectedOrganizationId, { strategy_types: 'CONSENT', days_ago: 7, limit: 500 })
        .catch(() => null),
      api
        .queryCallsV3(selectedOrganizationId, { strategy_types: 'CHECK_IN', days_ago: 7, limit: 500 })
        .catch(() => null),
    ]).then(([consentCalls, checkinCalls]) => {
      if (cancelled) return;
      setConsentsByDay(groupCallsByDay(consentCalls?.calls ?? [], 7));
      setCheckinsByDay(groupCallsByDay(checkinCalls?.calls ?? [], 7));
      setChartsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedOrganizationId]);

  return (
    <div className="space-y-6">
      {statsLoading ? (
        <CardGridSkeleton cards={4} />
      ) : (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Patients"
            value={stats?.patients ?? 0}
            icon={<Users className="h-5 w-5" />}
          />
          <StatCard
            title="Patients Consented"
            value={Math.max(stats?.apcmConsentedPatients ?? 0, stats?.consentedByState ?? 0)}
            subtitle={
              stats?.patients
                ? `${Math.round((Math.max(stats.apcmConsentedPatients, stats.consentedByState) / stats.patients) * 100)}% of total`
                : undefined
            }
            icon={<FileCheck className="h-5 w-5" />}
          />
          <StatCard
            title="Calls Made"
            value={stats?.totalCalls ?? 0}
            icon={<Phone className="h-5 w-5" />}
          />
          <StatCard
            title="Check-ins This Month"
            value={Math.max(stats?.checkinsThisMonth ?? 0, stats?.checkinsCompletedByState ?? 0)}
            subtitle={
              stats?.approvedByState
                ? `${stats.approvedByState} approved care plans`
                : undefined
            }
            icon={<CalendarCheck className="h-5 w-5" />}
          />
        </div>
      )}

      {chartsLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <ChartSkeleton />
          <ChartSkeleton />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <DailyBarChart
            title="Consent Calls (Last 7 Days)"
            data={consentsByDay}
            color="#6b7a99"
          />
          <DailyBarChart
            title="Check-in Calls (Last 7 Days)"
            data={checkinsByDay}
            color="#7a9e9e"
          />
        </div>
      )}

      <div className="flex items-center justify-center pt-4">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-3">
            Customize this dashboard by adding analytics cards.
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/analytics">
              <BarChart3 className="mr-2 h-4 w-4" />
              Go to Analytics
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
